import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { 
  Video, Mic, MicOff, VideoOff, Shield, Users, 
  ArrowRight, Sparkles, Scale, QrCode, Lock, CheckCircle2, 
  Settings, Disc, Globe, MessageSquare, PictureInPicture
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ElumVideoConferenceModal from '../components/ElumVideoConferenceModal';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

export const VideoConferencePage: React.FC = () => {
  const { user } = useAuth();
  const { colors } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [meetingIdInput, setMeetingIdInput] = useState('');
  const [passcodeInput, setPasscodeInput] = useState('elum2026');
  const [meetingTopicInput, setMeetingTopicInput] = useState('Judicial Case Review & Strategy Conference');
  const [userNameInput, setUserNameInput] = useState(user?.name || 'Advocate M. Kalat');
  
  // Pre-Meeting Audio / Video Test
  const [isLobbyMuted, setIsLobbyMuted] = useState(false);
  const [isLobbyVideoOff, setIsLobbyVideoOff] = useState(false);
  const lobbyVideoRef = useRef<HTMLVideoElement | null>(null);
  const lobbyStreamRef = useRef<MediaStream | null>(null);

  // Active Meeting Modal State
  const [isConferenceOpen, setIsConferenceOpen] = useState(false);
  const [activeMeetingId, setActiveMeetingId] = useState('');
  const [activeMeetingTopic, setActiveMeetingTopic] = useState('');

  // 1. Check URL query parameters for auto-join
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mId = params.get('meetingId') || params.get('id');
    const pwd = params.get('pwd') || params.get('passcode');
    const topic = params.get('topic');

    if (mId) {
      setMeetingIdInput(mId);
      if (pwd) setPasscodeInput(pwd);
      if (topic) setMeetingTopicInput(topic);
      // Automatically launch meeting if specified in query
      setActiveMeetingId(mId);
      setActiveMeetingTopic(topic || 'ELum Judicial & Legal Strategy Conference');
      setIsConferenceOpen(true);
    }
  }, [location.search]);

  // 2. Pre-Meeting AV Test Stream
  useEffect(() => {
    let stream: MediaStream | null = null;
    const initLobbyMedia = async () => {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 640 }, height: { ideal: 360 } },
            audio: true
          });
          lobbyStreamRef.current = stream;
          if (lobbyVideoRef.current) {
            lobbyVideoRef.current.srcObject = stream;
          }
        }
      } catch (err) {
        console.warn('Lobby camera preview note:', err);
      }
    };

    if (!isConferenceOpen) {
      initLobbyMedia();
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach(t => t.stop());
      }
    };
  }, [isConferenceOpen]);

  const toggleLobbyVideo = () => {
    if (lobbyStreamRef.current) {
      lobbyStreamRef.current.getVideoTracks().forEach(t => {
        t.enabled = isLobbyVideoOff;
      });
    }
    setIsLobbyVideoOff(!isLobbyVideoOff);
  };

  const toggleLobbyAudio = () => {
    if (lobbyStreamRef.current) {
      lobbyStreamRef.current.getAudioTracks().forEach(t => {
        t.enabled = isLobbyMuted;
      });
    }
    setIsLobbyMuted(!isLobbyMuted);
  };

  const handleStartInstantMeeting = () => {
    const newId = `${Math.floor(100 + Math.random() * 900)} ${Math.floor(100 + Math.random() * 900)} ${Math.floor(1000 + Math.random() * 9000)}`;
    setActiveMeetingId(newId);
    setActiveMeetingTopic(meetingTopicInput || 'ELum Judicial Strategy Conference');
    setIsConferenceOpen(true);
  };

  const handleJoinMeeting = (e: React.FormEvent) => {
    e.preventDefault();
    if (!meetingIdInput.trim()) return;
    setActiveMeetingId(meetingIdInput.trim());
    setActiveMeetingTopic(meetingTopicInput || 'Judicial Video Conference');
    setIsConferenceOpen(true);
  };

  const isSuperUserOrAdmin = Boolean(
    user?.role === 'superuser' || 
    user?.role === 'admin' || 
    user?.email === 'elumlawassociates@gmail.com' || 
    (user as any)?.canRecord === true
  );

  return (
    <div style={{ backgroundColor: '#020617', color: '#f8fafc', minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Header />

      <main style={{ flex: 1, padding: '2.5rem 1.5rem', maxWidth: '1280px', margin: '0 auto', width: '100%' }}>
        
        {/* HERO TITLE & PLATFORM BADGE */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'rgba(2, 132, 199, 0.15)', border: '1px solid #0284c7', padding: '0.4rem 1rem', borderRadius: '30px', color: '#38bdf8', fontSize: '0.85rem', fontWeight: '800', marginBottom: '1rem' }}>
            <Sparkles size={16} color="#ecc94b" />
            ELum Judicial Zoom Suite • 256-Bit TLS End-to-End Encrypted
          </div>
          <h1 style={{ fontSize: '2.5rem', fontWeight: '900', color: '#f8fafc', margin: '0 0 0.75rem 0', letterSpacing: '-0.5px' }}>
            High-Definition Legal Video Conference
          </h1>
          <p style={{ fontSize: '1.05rem', color: '#94a3b8', maxWidth: '750px', margin: '0 auto', lineHeight: '1.6' }}>
            Secure judicial hearings, client consultations, evidence reviews, and strategy sessions with <strong>Window-in-Window (PiP) multitasking</strong>, real-time screen sharing, interactive case annotation, and superuser-governed audio/video recording.
          </p>
        </div>

        {/* MAIN LOBBY GRID */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '2rem', alignItems: 'start' }}>
          
          {/* LEFT: PRE-MEETING AV TEST VIEWPORT */}
          <div style={{ backgroundColor: '#0f172a', borderRadius: '20px', border: '1px solid #1e293b', padding: '1.5rem', boxShadow: '0 12px 32px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#ecc94b', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Video size={16} /> Pre-Meeting Camera & Audio Test
              </span>
              <span style={{ fontSize: '0.75rem', backgroundColor: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', padding: '0.2rem 0.5rem', borderRadius: '4px', fontWeight: '700' }}>
                Devices Ready
              </span>
            </div>

            {/* Video Viewport */}
            <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', backgroundColor: '#020617', borderRadius: '12px', overflow: 'hidden', border: '2px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
              {!isLobbyVideoOff ? (
                <video 
                  ref={lobbyVideoRef}
                  autoPlay 
                  playsInline 
                  muted 
                  style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} 
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                  <img 
                    src={(user as any)?.avatarUrl || '/owner.jpeg'} 
                    alt="Avatar" 
                    style={{ width: '70px', height: '70px', borderRadius: '50%', border: '2px solid #0284c7' }} 
                  />
                  <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#94a3b8' }}>Camera is Off</span>
                </div>
              )}

              {/* Lobby Status Pill */}
              <div style={{ position: 'absolute', bottom: '10px', left: '10px', backgroundColor: 'rgba(0,0,0,0.7)', padding: '0.25rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', color: 'white', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                {isLobbyMuted ? <MicOff size={13} color="#ef4444" /> : <Mic size={13} color="#22c55e" />}
                <span>{userNameInput || 'Advocate'}</span>
              </div>
            </div>

            {/* Lobby Controls */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <button 
                onClick={toggleLobbyAudio}
                style={{ flex: 1, backgroundColor: isLobbyMuted ? '#ef4444' : '#1e293b', color: 'white', border: '1px solid #334155', borderRadius: '8px', padding: '0.6rem', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
              >
                {isLobbyMuted ? <><MicOff size={16} /> Mic Off</> : <><Mic size={16} color="#22c55e" /> Mic On</>}
              </button>

              <button 
                onClick={toggleLobbyVideo}
                style={{ flex: 1, backgroundColor: isLobbyVideoOff ? '#ef4444' : '#1e293b', color: 'white', border: '1px solid #334155', borderRadius: '8px', padding: '0.6rem', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
              >
                {isLobbyVideoOff ? <><VideoOff size={16} /> Video Off</> : <><Video size={16} color="#22c55e" /> Video On</>}
              </button>
            </div>

            {/* Feature Badges List */}
            <div style={{ borderTop: '1px solid #1e293b', paddingTop: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.78rem', color: '#94a3b8' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <CheckCircle2 size={14} color="#22c55e" /> Window-in-Window (PiP)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <CheckCircle2 size={14} color="#22c55e" /> Live Screen Sharing
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <CheckCircle2 size={14} color="#22c55e" /> Case Annotation Canvas
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <CheckCircle2 size={14} color="#22c55e" /> Superuser Recording RBAC
              </div>
            </div>
          </div>

          {/* RIGHT: START / JOIN MEETING FORMS */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* CARD 1: START INSTANT JUDICIAL MEETING */}
            <div style={{ backgroundColor: '#0f172a', borderRadius: '20px', border: '1px solid #0284c7', padding: '1.75rem', boxShadow: '0 8px 24px rgba(2, 132, 199, 0.15)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                <div style={{ backgroundColor: '#0284c7', color: 'white', width: '36px', height: '36px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Video size={20} />
                </div>
                <div>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                    Start Instant Video Conference
                  </h2>
                  <span style={{ fontSize: '0.78rem', color: '#38bdf8', fontWeight: '700' }}>Host as Advocate / Chambers Lead</span>
                </div>
              </div>

              <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: '0.75rem 0 1.25rem 0', lineHeight: '1.5' }}>
                Launch a secured conference room with auto-generated Meeting ID, invitation link, and instant QR Code share.
              </p>

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '0.35rem' }}>Conference Topic / Cause List:</label>
                <input 
                  type="text" 
                  value={meetingTopicInput} 
                  onChange={(e) => setMeetingTopicInput(e.target.value)}
                  placeholder="e.g. Constitutional Writ Petition 2026 Hearing Strategy"
                  style={{ width: '100%', backgroundColor: '#020617', border: '1px solid #334155', borderRadius: '8px', padding: '0.65rem 0.85rem', color: 'white', fontSize: '0.85rem', outline: 'none' }}
                />
              </div>

              <button 
                onClick={handleStartInstantMeeting}
                style={{ width: '100%', backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.85rem', borderRadius: '10px', fontWeight: '900', fontSize: '0.95rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', boxShadow: '0 4px 14px rgba(2, 132, 199, 0.4)' }}
              >
                <Video size={18} /> Launch Instant Conference
              </button>
            </div>

            {/* CARD 2: JOIN EXISTING MEETING BY ID */}
            <div style={{ backgroundColor: '#0f172a', borderRadius: '20px', border: '1px solid #1e293b', padding: '1.75rem', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                <div style={{ backgroundColor: '#7c3aed', color: 'white', width: '36px', height: '36px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Users size={20} />
                </div>
                <div>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: '900', color: '#f8fafc', margin: 0 }}>
                    Join Meeting by ID
                  </h2>
                  <span style={{ fontSize: '0.78rem', color: '#c084fc', fontWeight: '700' }}>Enter 10-Digit ID or Meeting Link</span>
                </div>
              </div>

              <form onSubmit={handleJoinMeeting} style={{ marginTop: '1.25rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '0.35rem' }}>Meeting ID or Link:</label>
                  <input 
                    type="text" 
                    value={meetingIdInput} 
                    onChange={(e) => setMeetingIdInput(e.target.value)}
                    placeholder="e.g. 849 392 1928"
                    required
                    style={{ width: '100%', backgroundColor: '#020617', border: '1px solid #334155', borderRadius: '8px', padding: '0.65rem 0.85rem', color: 'white', fontSize: '0.85rem', outline: 'none' }}
                  />
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#cbd5e1', marginBottom: '0.35rem' }}>Passcode (If required):</label>
                  <input 
                    type="password" 
                    value={passcodeInput} 
                    onChange={(e) => setPasscodeInput(e.target.value)}
                    placeholder="elum2026"
                    style={{ width: '100%', backgroundColor: '#020617', border: '1px solid #334155', borderRadius: '8px', padding: '0.65rem 0.85rem', color: 'white', fontSize: '0.85rem', outline: 'none' }}
                  />
                </div>

                <button 
                  type="submit"
                  style={{ width: '100%', backgroundColor: '#7c3aed', color: 'white', border: 'none', padding: '0.85rem', borderRadius: '10px', fontWeight: '900', fontSize: '0.95rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', boxShadow: '0 4px 14px rgba(124, 58, 237, 0.4)' }}
                >
                  <ArrowRight size={18} /> Join Video Conference
                </button>
              </form>
            </div>

          </div>

        </div>

      </main>

      {/* ACTIVE ZOOM-LIKE VIDEO CONFERENCE MODAL */}
      <ElumVideoConferenceModal 
        isOpen={isConferenceOpen}
        onClose={() => setIsConferenceOpen(false)}
        meetingId={activeMeetingId}
        meetingTopic={activeMeetingTopic}
        isHost={true}
      />

      <Footer />
    </div>
  );
};

export default VideoConferencePage;
